import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, filter, map } from 'rxjs';
import {
	AccessNumber,
	AccessNumberListItem
} from 'src/app/api/access-number-api/models';
import { AccessNumberService } from 'src/app/api/access-number-api/services';

@Injectable({
	providedIn: 'root'
})
export class AccessNumbersGlobalService {
	private accessNumberService = inject(AccessNumberService);

	public globalAccessNumbers$: Observable<AccessNumberListItem[]>;
	public activeGlobalAccessNumbers$: Observable<AccessNumberListItem[]>;

	private globalAccessNumbers: BehaviorSubject<AccessNumberListItem[]> =
		new BehaviorSubject<AccessNumberListItem[]>(null);

	constructor() {
		this.globalAccessNumbers$ = this.globalAccessNumbers
			.asObservable()
			.pipe(
				filter((value) => {
					return value != null;
				})
			);

		this.activeGlobalAccessNumbers$ = this.globalAccessNumbers
			.asObservable()
			.pipe(
				filter((value) => value != null),
				map((accessNumbers) =>
					accessNumbers.filter(
						(accessNumber) => accessNumber.inactive === false
					)
				)
			);
	}

	public async init(): Promise<void> {
		await this.getAllAccessNumbers();
	}

	public addAccessNumber(number: AccessNumber): void {
		this.globalAccessNumbers.value.push(this.getMinified(number));

		this.globalAccessNumbers.next(
			this.globalAccessNumbers.value.sort((a, b) =>
				a.countryDetails.name.localeCompare(b.countryDetails.name)
			)
		);
	}

	public setAccessNumber(index: number, number: AccessNumber): void {
		this.globalAccessNumbers.value[index] = this.getMinified(number);
		this.globalAccessNumbers.next(this.globalAccessNumbers.value);
	}

	public findAccessNumberIndex(id: string): number {
		return this.globalAccessNumbers.value.findIndex(
			(accessNumber) => id == accessNumber.id
		);
	}

	public removeAccessNumber(number: AccessNumber): void {
		this.globalAccessNumbers.next(
			this.globalAccessNumbers.value.filter((s) => s.id != number.id)
		);
	}

	public findAccessNumber(id: string): AccessNumberListItem {
		return this.globalAccessNumbers?.value.find((e) => e.id == id);
	}

	private async getAllAccessNumbers(): Promise<void> {
		await this.accessNumberService
			.getAccessnumberMinifiedV2$Json()
			.toPromise()
			.then((numbers) => {
				this.globalAccessNumbers.next(numbers);
			});
	}

	private getMinified(number: AccessNumber): AccessNumberListItem {
		return {
			id: number.id,
			countryDetails: number.countryDetails,
			inactive: number.inactive,
			languages: number.languages,
			localProvider: number.localProvider,
			providerNote: number.providerNote,
			emails: number.emails.map((email) => ({
				note: email.note,
				notes: email.notes ?? [],
				emailAddress: email.emailAddress
			})),
			internationalNumbers: number.internationalNumbers.map((num) => ({
				note: num.note,
				notes: num.notes ?? [],
				number: num.number
			})),
			tollFreeNumbers: number.tollFreeNumbers.map((num) => ({
				note: num.note,
				notes: num.notes ?? [],
				number: num.number
			}))
		};
	}
}
